<template>
  <standalone-call
    v-if="isCallFormOpen"
    @close="closeCallForm"
    @call:start="handleCall"
  />
  <teleport v-if="callDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="closeCallDialog"
    />
  </teleport>
  <send-deal-communication-modal
    v-if="communicationModalType"
    :type="communicationModalType"
    standalone
    anchor-bottom-left
    @close-dialog="closeCommunicationModal"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { CallErrorReason } from "@/enums/communicationLogs";
import { CommunicationProvider } from "@/enums/communicationProviders";
import { setActiveCallSession } from "@/helpers/communication";
import { DEFAULT_DIALER } from "@/helpers/constants";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useContacts } from "@/hooks/contacts";
import { dialerApi } from "@/services/dialer-api";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";
import useQuickActionsStore from "@/stores/quickActions";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import StandaloneCall from "@/components/communication/standalone/StandaloneCall.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";

const { trackEvent } = useAnalyticsStore();
const quickActionsStore = useQuickActionsStore();
const callsStore = useCallsStore();
const { preferredDialer } = useTwilio();
const { callClientStandalone } = useContacts();
const emitter = useEmitter();

const { communicationModalType, isCallFormOpen, callDialog } =
  storeToRefs(quickActionsStore);
const {
  closeCommunicationModal,
  closeCallForm,
  openCallDialog,
  closeCallDialog,
} = quickActionsStore;
const { isPiPOpen } = storeToRefs(callsStore);

const handleCall = async (phoneNumber: string) => {
  const result = await callClientStandalone({
    to: phoneNumber,
    provider: preferredDialer.value ?? DEFAULT_DIALER,
  });
  if (!result?.connectionLog?.id) {
    trackEvent(AnalyticsEvent.CallOutError, {
      reason: CallErrorReason.StandaloneCallMissingApiResponse,
    });
    return;
  }
  trackEvent(AnalyticsEvent.QuickActionsCallInitiated, {
    provider:
      result.callOutPayload?.provider ??
      preferredDialer.value ??
      DEFAULT_DIALER,
  });
  closeCallForm();
  setActiveCallSession(result);

  // Auto-open PiP for Twilio calls only when this browser is the active dialer
  if (
    result.callOutPayload?.provider === CommunicationProvider.Twilio &&
    !isPiPOpen.value &&
    dialerApi.isActiveDialer.value
  ) {
    emitter.emit(TWILIO_PIP_OPEN);
  }
  openCallDialog({ log: result.connectionLog });
};
</script>
